import pubsub from '../../shared/utilities/pubsub';
import { generateId } from '../../shared/utilities/id';
import { camelCaseToDashCase, dashCaseToCamelCase } from '../../shared/utilities/case';
import { getBreakpoint } from '../../shared/utilities/screenSize';
import { debounce } from '../../shared/utilities/debounce';

export default class BaseBehavior {
	constructor(element) {
		this.element = element;
		this.state = undefined;
		this.events = {};
		this.subscriptions = {};
		this.behaviorName = camelCaseToDashCase(element.getAttribute('data-behavior'));
		this.instanceId = generateId();
		this.attachInstanceId();
	}

	setState(partialState) {
		Object.keys(partialState).forEach(key => {
			if (!{}.hasOwnProperty.call(this.state, key)) {
				throw new Error(
					`${this.behaviorName} does not have ${key} on the state object,
					make sure to define all state properties in the constructor.`
				);
			}
		});

		this.state = { ...(this.state || {}), ...partialState };
	}

	attachInstanceId() {
		this.element.setAttribute('data-instance', `${this.behaviorName}__${this.instanceId}`);
		this.element.removeAttribute('data-instance-init');
	}

	initDomCache() {
		this.dom = {};

		// Filter out ref's that embedded in another instance of the same behavior.
		Array.from(this.element.querySelectorAll(`[data-ref^=${this.behaviorName}]`))
			.filter(el => el.closest(`[data-behavior*="${this.behaviorName}"]`) === this.element)
			.forEach(element => {
				const ref = element.getAttribute('data-ref');
				const key = dashCaseToCamelCase(ref).split('__')[1];

				if (key) {
					this.dom[key] = element;
				} else {
					console.error(
						`Element cannot be cached. Reference "${ref}" should have the format "behavior-name__element-name"`
					);
				}
			});
	}

	initDomCacheFirstBehavior() {
		this.dom = {};

		// Filter out ref's that embedded in another instance of the same behavior.
		Array.from(this.element.querySelectorAll(`[data-ref^=${this.behaviorName.split(' ')[0]}]`))
			.filter(el => el.closest(`[data-behavior*="${this.behaviorName}"]`) === this.element)
			.forEach(element => {
				const ref = element.getAttribute('data-ref');
				const refKey = dashCaseToCamelCase(ref).split('__')[1];

				if (refKey) {
					this.dom[refKey] = element;
				} else {
					console.error(
						`Element cannot be cached. Reference "${ref}" should have the format "behavior-name__element-name"`
					);
				}
			});
	}

	publishBreakpointOnResize(delay = 250) {
		if (this.breakpoint) return;

		this.breakpoint = getBreakpoint();
		this.publish('SITE.BREAKPOINT', this.breakpoint);

		const debouncedGetBreakpoint = debounce(() => {
			const newBreakPoint = getBreakpoint();
			if (this.breakpoint !== newBreakPoint) {
				this.publish('SITE.BREAKPOINT', newBreakPoint);
				this.breakpoint = newBreakPoint;
			}
		}, delay);

		window.addEventListener('resize', debouncedGetBreakpoint);
	}

	addEventListener(element, event, fn, useCapture = false) {
		if (element) {
			this.events[event] = fn;
			element.addEventListener(event.split('.')[0], this.events[event], useCapture);
		}
	}

	removeEventListener(element, event) {
		element.removeEventListener(event.split('.')[0], this.events[event]);
		this.events[event] = null;
		delete this.events[event];
	}

	// eslint-disable-next-line class-methods-use-this
	publish(topic, obj) {
		pubsub.publish(topic, obj);
	}

	subscribe(topic, fn) {
		this.subscriptions[topic] = fn;
		pubsub.subscribe(topic, fn);
	}

	unsubscribe(topic) {
		pubsub.unsubscribe(topic, this.subscriptions[topic]);
		this.subscriptions[topic] = null;
		delete this.subscriptions[topic];
	}
}

// Below code is added for fixing scrolling issue on page refresh.
window.addEventListener('load', () => {
	setTimeout(() => {
		const navigation = document.querySelector('#navigation');
		// eslint-disable-next-line
		if (navigation && navigation.getBoundingClientRect().bottom === scrollY) {
			window.scroll(0, 0);
		}
	}, 0);
});

process.on('unhandledRejection', (reason, promise) => {
	// eslint-disable-next-line no-console
	console.log('Reason for this failure is : ', reason);
	// eslint-disable-next-line no-console
	console.log('promise is', promise);
	// Application specific logging, throwing an error, or other logic here
});
